import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';
import type { AuditLogEntry } from '@/lib/types';

function JsonBlock({ label, value }: { label: string; value: unknown }): JSX.Element {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </div>
      <pre className="max-h-64 overflow-auto rounded border border-slate-200 bg-white p-2 font-mono text-[11px] text-slate-700">
        {value === null || value === undefined ? '—' : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}

export function AuditLog(): JSX.Element {
  const [expanded, setExpanded] = useState<string | null>(null);
  const log = useQuery({
    queryKey: ['/api/admin/audit-log'],
    queryFn: () => api.get<AuditLogEntry[]>('/api/admin/audit-log?limit=200'),
  });

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Audit Log</h2>
      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="w-8 px-4 py-2.5"></th>
              <th className="px-4 py-2.5">Időpont</th>
              <th className="px-4 py-2.5">Művelet</th>
              <th className="px-4 py-2.5">Cél típus</th>
              <th className="px-4 py-2.5">Cél ID</th>
              <th className="px-4 py-2.5">Principal</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-xs">
            {log.isLoading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Betöltés…</td></tr>
            )}
            {log.isError && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-rose-600">Hiba: {String(log.error)}</td></tr>
            )}
            {log.data?.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Nincs bejegyzés.</td></tr>
            )}
            {log.data?.map((e) => {
              const isOpen = expanded === e.id;
              return (
                <Fragment key={e.id}>
                  <tr
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setExpanded(isOpen ? null : e.id)}
                  >
                    <td className="px-4 py-2.5 text-slate-400">
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 font-mono">{formatDate(e.timestamp)}</td>
                    <td className="px-4 py-2.5 font-mono font-semibold">{e.action}</td>
                    <td className="px-4 py-2.5 font-mono">{e.targetType ?? '—'}</td>
                    <td className="px-4 py-2.5 font-mono">{e.targetId ?? '—'}</td>
                    <td className="px-4 py-2.5 font-mono">{e.principal ?? 'system'}</td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50">
                      <td colSpan={6} className="px-4 py-3">
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                          <JsonBlock label="Before" value={e.before} />
                          <JsonBlock label="After" value={e.after} />
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
